import { partials } from "@shopify/partial-rendering";
import { transition } from "view-transition";

// <s-product-recommendations> lazily fetches related/complementary products and swaps them
// into its {% partial %} region using the base theme's partial-rendering stack — the same
// @shopify/partial-rendering + view-transition that <s-live-form> (filters) is built on.
// One component per file, framework-free.
//
// When the block was already rendered in the recommendations context (data-performed="true"),
// the server markup is kept and no fetch happens. Otherwise, once the block nears the
// viewport, it fetches data-url and applies the named partial, replacing the skeletons.
class ProductRecommendations extends HTMLElement {
  #observer = null;
  #controller = null;

  connectedCallback() {
    if (this.dataset.performed === "true") {
      this.#syncVisibility();
      return;
    }
    if (!this.dataset.url || !this.dataset.productId || !this.dataset.partial) return;

    this.#observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0]?.isIntersecting) return;
        observer.disconnect();
        this.#load();
      },
      { rootMargin: "0px 0px 400px 0px" },
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#controller?.abort();
  }

  async #load() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const url = `${this.dataset.url}&product_id=${encodeURIComponent(this.dataset.productId)}`;
    const name = this.dataset.partial;

    this.setAttribute("aria-busy", "true");
    try {
      // Fetch the named {% partial %} region from the recommendations endpoint and apply it.
      const html = await partials.fetch(name, { url, signal });
      // Keep the server-rendered fallback (same-collection products) in case Shopify returns no recommendations.
      const fallback = this.#hasFallback() ? this.#region()?.range.cloneContents() : null;
      await transition(() => partials.apply(html));
      this.dataset.performed = "true";
      if (fallback && this.#isEmpty()) {
        const range = this.#region()?.range;
        range?.insertNode(fallback);
      }
      this.#syncVisibility();
    } catch (error) {
      // No recommendations / fetch failed: keep the fallback grid if there is one, else collapse quietly.
      if (error.name !== "AbortError" && !this.#hasFallback()) this.hidden = true;
    } finally {
      this.removeAttribute("aria-busy");
      this.#controller = null;
    }
  }

  #hasFallback() {
    return this.dataset.hasFallback === "true";
  }

  #region() {
    return partials.getAll(this).find(({ name }) => name === this.dataset.partial);
  }

  #isEmpty() {
    const content = this.#region()?.range.cloneContents();
    return !content || (!content.firstElementChild && !content.textContent.trim());
  }

  #syncVisibility() {
    this.hidden = this.#isEmpty();
  }
}

if (!customElements.get("s-product-recommendations")) {
  customElements.define("s-product-recommendations", ProductRecommendations);
}
